import { useQuery } from '@tanstack/react-query';
import { insightsApi } from '../api/insights';
import { Card } from '../components/Card';
import { KpiCard } from '../components/KpiCard';
import { Pill } from '../components/Pill';
import { DataTable, type Column } from '../components/DataTable';
import { fmtNum } from '../lib/format';
import type { PricingHealthDto } from '../api/types';

type SampleCard = PricingHealthDto['sampleUnpricedCards'][number];

export function PricingScreen() {
  const { data, isFetching, error } = useQuery({
    queryKey: ['pricing-health', 25],
    queryFn: () => insightsApi.pricingHealth(25),
  });

  const d = data;
  const coverage = d?.computedCoveragePct ?? 0;
  const split = d?.listingSourceSplit;
  const splitTotal = split ? split.computed + split.scrydexFallbackOnly : 0;

  const cardColumns: Column<SampleCard>[] = [
    {
      key: 'card',
      header: 'Card',
      render: (r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{r.name ?? r.cardId}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.setName ?? '—'}</div>
        </div>
      ),
    },
    {
      key: 'cardId',
      header: 'Card ID',
      align: 'right',
      hideMobile: true,
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)' }}>{r.cardId}</span>,
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '24px 28px' }}>
        <Card padding={20}>
          <span style={{ color: 'var(--bad)', fontSize: 13 }}>{(error as Error).message}</span>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
        <KpiCard
          label="Computed coverage"
          value={coverage}
          format={(n) => `${n.toFixed(1)}%`}
          decimals={1}
          accent
        />
        <KpiCard label="Total cards" value={d?.totalCards ?? 0} />
        <KpiCard label="With computed price" value={d?.cardsWithComputedPrice ?? 0} />
        <KpiCard
          label="Without computed"
          value={d?.cardsWithoutComputedPrice ?? 0}
          sublabel="fall back to Scrydex"
        />
        <KpiCard
          label="Stale computed"
          value={d?.staleComputed.count ?? 0}
          sublabel={d ? `older than ${d.staleComputed.staleDays}d` : undefined}
        />
      </div>

      {/* Coverage bar */}
      <Card padding={18}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15 }}>Price-engine coverage</div>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{coverage.toFixed(1)}%</span>
        </div>
        <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', background: 'var(--bg-sunken)' }}>
          <div style={{ width: `${Math.min(100, Math.max(0, coverage))}%`, background: 'var(--brand)' }} />
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>
          {fmtNum(d?.cardsWithComputedPrice ?? 0)} of {fmtNum(d?.totalCards ?? 0)} cards priced by the engine
        </div>
      </Card>

      {/* Listing source split */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        <Card padding={18}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, marginBottom: 12 }}>
            Listing source split
          </div>
          <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: 'var(--bg-sunken)', marginBottom: 12 }}>
            <div style={{ width: `${splitTotal > 0 ? ((split?.computed ?? 0) / splitTotal) * 100 : 0}%`, background: 'var(--brand)' }} />
            <div style={{ width: `${splitTotal > 0 ? ((split?.scrydexFallbackOnly ?? 0) / splitTotal) * 100 : 0}%`, background: 'var(--warn)' }} />
          </div>
          <SplitRow color="var(--brand)" label="Engine-computed" value={split?.computed ?? 0} />
          <SplitRow color="var(--warn)" label="Scrydex fallback only" value={split?.scrydexFallbackOnly ?? 0} />
        </Card>

        <Card padding={18}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
            Stale computed prices
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>
            Computed values not refreshed in the last {d?.staleComputed.staleDays ?? 0} days
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 32, letterSpacing: '-0.03em', color: 'var(--ink)' }}>
            {fmtNum(d?.staleComputed.count ?? 0)}
          </div>
          <div style={{ marginTop: 8 }}>
            {(d?.staleComputed.count ?? 0) === 0 ? (
              <Pill tone="good" size="sm">all fresh</Pill>
            ) : (
              <Pill tone="warn" size="sm">needs a re-run</Pill>
            )}
          </div>
        </Card>
      </div>

      <Card padding={0}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Unpriced card sample</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            Example cards with no engine-computed price (sampled)
          </div>
        </div>
        <DataTable
          rows={d?.sampleUnpricedCards ?? []}
          columns={cardColumns}
          rowKey={(r) => r.cardId}
          loading={isFetching}
          pageSize={25}
          emptyState="Every card has a computed price."
        />
      </Card>

      <Card padding={0}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Phantom-variant cards</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
              Data-hygiene warning · variants priced but likely non-existent
            </div>
          </div>
          {d && d.phantomVariantCards.length > 0 && (
            <span style={{ marginLeft: 'auto' }}>
              <Pill tone="warn" size="sm">{fmtNum(d.phantomVariantCards.length)} flagged</Pill>
            </span>
          )}
        </div>
        <DataTable
          rows={d?.phantomVariantCards ?? []}
          columns={cardColumns}
          rowKey={(r) => r.cardId}
          loading={isFetching}
          pageSize={25}
          emptyState="No phantom variants detected."
        />
      </Card>
    </div>
  );
}

function SplitRow({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
      <span style={{ fontSize: 13, color: 'var(--ink-2)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
        {label}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink)' }}>{fmtNum(value)}</span>
    </div>
  );
}
